import { useRef, useState } from "react";
import { Loader2, Upload, ShieldCheck, X } from "lucide-react";
import { compressImage } from "../lib/compressImage";

/**
 * V25 Batch B — Aadhaar image picker for the public Tutor Application form.
 * Compresses to JPEG (max 1280px edge, q=82) before handing the base64 string
 * to the parent form.
 *
 * Props:
 *  - label: "Aadhaar — Front Side" etc.
 *  - value: current { b64, mime, name, sizeBytes } or null
 *  - onChange(value | null)
 *  - testid: data-testid prefix
 *  - required
 */
export default function AadhaarImageInput({ label, value, onChange, testid, required = true }) {
    const fileRef = useRef(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    const pick = () => fileRef.current?.click();

    const handle = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            setError("Only JPG / PNG / WebP images accepted.");
            if (fileRef.current) fileRef.current.value = "";
            return;
        }
        if (file.size > 8 * 1024 * 1024) {
            setError("Image must be smaller than 8 MB.");
            if (fileRef.current) fileRef.current.value = "";
            return;
        }
        setBusy(true);
        setError("");
        try {
            const out = await compressImage(file, { maxEdge: 1280, quality: 0.82 });
            onChange({ ...out, name: file.name });
        } catch (err) {
            setError(err.message || "Failed to process image.");
        } finally {
            setBusy(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    const clear = () => {
        onChange(null);
        setError("");
    };

    const previewSrc = value ? `data:${value.mime};base64,${value.b64}` : null;

    return (
        <div className="space-y-1.5" data-testid={`${testid}-wrapper`}>
            <div className="flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">
                    {label}{required ? " *" : ""}
                </span>
                {value && (
                    <button
                        type="button"
                        onClick={clear}
                        data-testid={`${testid}-clear`}
                        className="text-xs text-slate-500 hover:text-red-600 inline-flex items-center gap-1"
                    >
                        <X className="w-3 h-3" /> Remove
                    </button>
                )}
            </div>
            {previewSrc ? (
                <div className="relative">
                    <img
                        src={previewSrc}
                        alt={label}
                        className="w-full max-h-48 object-contain rounded-lg border border-slate-200 bg-slate-50"
                        data-testid={`${testid}-preview`}
                    />
                    <div className="mt-1 flex items-center justify-between text-[10px] text-emerald-700">
                        <span className="inline-flex items-center gap-1">
                            <ShieldCheck className="w-3 h-3" /> Ready to upload
                        </span>
                        <span className="text-slate-500">{(value.sizeBytes / 1024).toFixed(0)} KB</span>
                    </div>
                </div>
            ) : (
                <button
                    type="button"
                    onClick={pick}
                    disabled={busy}
                    data-testid={`${testid}-pick`}
                    className="w-full border-2 border-dashed border-slate-300 hover:border-[#1E3A8A] hover:bg-blue-50/40 rounded-lg px-3 py-4 inline-flex items-center justify-center gap-2 text-sm text-slate-600 disabled:opacity-60 transition-colors"
                >
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4 text-slate-400" />}
                    <span>{busy ? "Processing…" : "Tap to upload (JPG / PNG)"}</span>
                </button>
            )}
            <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/webp"
                onChange={handle}
                className="hidden"
                data-testid={`${testid}-input`}
            />
            {error && (
                <p className="text-[11px] text-red-600" data-testid={`${testid}-error`}>{error}</p>
            )}
        </div>
    );
}
